/**
 * 設定 > 通知 > チャット管理 (v3.2.21+ / 2026-10-26 に Chatwork を追加)。
 *
 * Google Chat と Chatwork の通知先をそれぞれ複数登録する。
 * - Google Chat: 名前 / Webhook URL / 説明 / 有効。業務通知テンプレ・管理者通知・経理担当通知が参照
 *   URL は `https://chat.googleapis.com/v1/spaces/.../messages?key=...&token=...` 形式
 * - Chatwork: 名前 / ルーム ID / Webhook URL (記録用) / 説明 / 有効。経理担当通知が参照
 *   送信は Chatwork API トークン (CHATWORK_API_TOKEN) + ルーム ID で行う
 */
import type { ChatEndpointKind } from '@goenhub/shared';
import { CHAT_ENDPOINT_KIND_LABELS, chatEndpointKind } from '@goenhub/shared';
import { Bot, MessagesSquare, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import {
  useChatEndpoints,
  useDeleteChatEndpoint,
  useUpsertChatEndpoint,
  type ChatEndpointRecord,
  type UpsertChatEndpointInput,
} from '@/hooks/useChatEndpoints';

interface EditState {
  open: boolean;
  kind: ChatEndpointKind;
  existing: ChatEndpointRecord | null;
}

const CLOSED: EditState = { open: false, kind: 'google_chat', existing: null };

export function ChatEndpointsTab() {
  const endpoints = useChatEndpoints();
  const upsert = useUpsertChatEndpoint();
  const del = useDeleteChatEndpoint();
  const [edit, setEdit] = useState<EditState>(CLOSED);

  const handleDelete = async (ep: ChatEndpointRecord) => {
    if (
      !confirm(
        `通知先「${ep.name}」を削除します。\n参照中の通知では送信されなくなります。よろしいですか？`,
      )
    )
      return;
    try {
      await del.mutateAsync(ep.id);
      toast.success('削除しました');
    } catch (err) {
      toast.error(`削除失敗: ${(err as Error).message}`);
    }
  };

  const all = endpoints.data ?? [];
  const sectionProps = {
    isLoading: endpoints.isLoading,
    onAdd: (kind: ChatEndpointKind) => setEdit({ open: true, kind, existing: null }),
    onEdit: (ep: ChatEndpointRecord) =>
      setEdit({ open: true, kind: chatEndpointKind(ep), existing: ep }),
    onDelete: handleDelete,
  };

  return (
    <div className="space-y-8">
      <EndpointSection
        kind="google_chat"
        rows={all.filter((ep) => chatEndpointKind(ep) === 'google_chat')}
        description={
          <>
            業務通知テンプレ (チャット通知)・管理者通知・経理担当への通知の送信先 Google Chat
            スペースを登録します。複数登録して、用途ごとに使い分けることができます。
            <br />
            URL 形式:{' '}
            <code className="font-mono">
              https://chat.googleapis.com/v1/spaces/.../messages?key=...&token=...
            </code>
          </>
        }
        {...sectionProps}
      />
      <EndpointSection
        kind="chatwork"
        rows={all.filter((ep) => chatEndpointKind(ep) === 'chatwork')}
        description={
          <>
            経理担当への通知 (営業・通知設定 &gt; 自動通知) の送信先 Chatwork ルームを登録します。
            <br />
            送信は Chatwork API トークン (設定 &gt; シークレット管理 の「Chatwork API トークン」)
            とルーム ID で行います。Webhook URL は記録用で、送信には使いません。
          </>
        }
        {...sectionProps}
      />

      <ChatEndpointDialog
        state={edit}
        onClose={() => setEdit(CLOSED)}
        onSave={async (input) => {
          try {
            await upsert.mutateAsync(input);
            toast.success('保存しました');
            setEdit(CLOSED);
          } catch (err) {
            toast.error(`保存失敗: ${(err as Error).message}`);
          }
        }}
      />
    </div>
  );
}

function EndpointSection({
  kind,
  rows,
  description,
  isLoading,
  onAdd,
  onEdit,
  onDelete,
}: {
  kind: ChatEndpointKind;
  rows: ChatEndpointRecord[];
  description: React.ReactNode;
  isLoading: boolean;
  onAdd: (kind: ChatEndpointKind) => void;
  onEdit: (ep: ChatEndpointRecord) => void;
  onDelete: (ep: ChatEndpointRecord) => void;
}) {
  const Icon = kind === 'chatwork' ? MessagesSquare : Bot;
  const label = CHAT_ENDPOINT_KIND_LABELS[kind];
  return (
    <section className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="flex items-center gap-1.5 text-sm font-bold text-[var(--text-primary)]">
            <Icon className="size-4 text-[var(--accent)]" />
            {label}
          </h3>
          <div className="text-xs text-[var(--text-muted)] leading-relaxed mt-1">{description}</div>
        </div>
        <Button variant="primary" size="sm" onClick={() => onAdd(kind)}>
          <Plus className="size-3.5" />
          新規追加
        </Button>
      </div>

      {isLoading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Icon}
          title={`${label} の通知先がありません`}
          description={
            kind === 'chatwork'
              ? '「新規追加」ボタンから Chatwork のルーム ID を登録してください。'
              : '「新規追加」ボタンから Google Chat の webhook URL を登録してください。'
          }
        />
      ) : (
        <div className="space-y-2">
          {rows.map((ep) => (
            <div
              key={ep.id}
              className="flex items-center gap-3 p-3 rounded-md border border-[var(--border)] bg-[var(--bg-surface)]"
            >
              <Icon
                className={`size-4 shrink-0 ${ep.isActive ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]'}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[var(--text-primary)] truncate">
                    {ep.name}
                  </span>
                  <Badge variant={ep.isActive ? 'approved' : 'default'}>
                    {ep.isActive ? '有効' : '無効'}
                  </Badge>
                </div>
                {ep.description && (
                  <p className="text-[11px] text-[var(--text-muted)] mt-0.5 line-clamp-1">
                    {ep.description}
                  </p>
                )}
                {kind === 'chatwork' && (
                  <p className="text-[11px] text-[var(--text-secondary)] mt-0.5">
                    ルーム ID: <span className="font-mono">{ep.roomId ?? '-'}</span>
                  </p>
                )}
                {ep.webhookUrl && (
                  <p className="text-[10px] text-[var(--text-muted)] font-mono truncate mt-0.5">
                    {ep.webhookUrl}
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onEdit(ep)}
                title="編集"
              >
                <Pencil className="size-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onDelete(ep)}
                title="削除"
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ChatEndpointDialog({
  state,
  onClose,
  onSave,
}: {
  state: EditState;
  onClose: () => void;
  onSave: (input: UpsertChatEndpointInput) => Promise<void>;
}) {
  const { open, kind, existing } = state;
  const isChatwork = kind === 'chatwork';
  const [name, setName] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [roomId, setRoomId] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (open) {
      setName(existing?.name ?? '');
      setWebhookUrl(existing?.webhookUrl ?? '');
      setRoomId(existing?.roomId ?? '');
      setDescription(existing?.description ?? '');
      setIsActive(existing?.isActive ?? true);
    }
  }, [open, existing]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('名前は必須です');
      return;
    }
    if (isChatwork) {
      if (!/^\d+$/.test(roomId.trim())) {
        toast.error('ルーム ID は数字で入力してください');
        return;
      }
    } else if (!webhookUrl.trim()) {
      toast.error('Webhook URL は必須です');
      return;
    }
    await onSave({
      id: existing?.id ?? null,
      kind,
      name: name.trim(),
      webhookUrl: webhookUrl.trim(),
      roomId: isChatwork ? roomId.trim() : null,
      description: description.trim() || null,
      isActive,
    });
  };

  const label = CHAT_ENDPOINT_KIND_LABELS[kind];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {existing ? `${label} の通知先を編集` : `${label} の通知先を新規追加`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">名前 *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: 社内一般 / 経理チーム"
            />
          </div>
          {isChatwork && (
            <div>
              <Label className="text-xs">ルーム ID *</Label>
              <Input
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                placeholder="例: 123456789"
                inputMode="numeric"
                className="font-mono text-[11px]"
              />
              <p className="text-[11px] text-[var(--text-muted)] mt-1">
                Chatwork でルームを開いたときの URL の「#!rid」の後ろの数字です。
              </p>
            </div>
          )}
          <div>
            <Label className="text-xs">Webhook URL {isChatwork ? '(任意・記録用)' : '*'}</Label>
            <Input
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder={isChatwork ? 'https://...' : 'https://chat.googleapis.com/v1/spaces/...'}
              className="font-mono text-[11px]"
            />
          </div>
          <div>
            <Label className="text-xs">説明 (任意)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="例: 経理担当への送金依頼通知用"
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <Label className="!mb-0 text-xs">有効化する</Label>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="button" variant="primary" onClick={handleSubmit}>
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
